import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PropsWithChildren, RefObject } from 'react'
import type { WillBeUnmounted } from '~/components/modal/Provider'
import { StackableFusuma } from '~/components/modal/StackableFusuma'
import { useIsPC } from '~/lib/ua'
import { useReserveNavigate } from '~/navigation'
import { css } from '~/styled-system/css'
import { vstack } from '~/styled-system/patterns'
import { Button } from '~/ui'
import { Courses } from './courses'
import { Menu, MenuTabs, TABS } from './parts'
import type { Tab } from './parts'
import type { Menus } from './query'

export function GuideMenus(props: {
  menus: NonNullable<Menus>
  willBeUnmounted: WillBeUnmounted
}) {
  const isPc = useIsPC()
  const navigate = useReserveNavigate()
  const onClick = useCallback(() => {
    navigate({ type: 'reserve' })
  }, [navigate])

  const GuideMenus = isPc ? PcGuideMenus : SdGuideMenus

  return (
    <StackableFusuma
      title="メニュー"
      headerColor={isPc ? 'white' : 'neutral'}
      layout="full"
      bodyClassName={vstack({ gap: '0', alignItems: 'stretch', h: 'full' })}
      willBeUnmounted={props.willBeUnmounted}
    >
      <GuideMenus menus={props.menus} />
      <div
        className={css({
          p: { base: '3', _pc: '4' },
          bgColor: 'white',
          shadow: 'outline.lg',
          isolation: 'isolate',
        })}
      >
        <Button color="primary" size={isPc ? 'lg' : 'md'} spread onClick={onClick}>
          空席確認・予約
        </Button>
      </div>
    </StackableFusuma>
  )
}

function ScrollableContentInFlex(props: PropsWithChildren) {
  return (
    <div className={css({ flex: '1', position: 'relative' })}>
      <div className={css({ position: 'absolute', inset: '0' })}>{props.children}</div>
    </div>
  )
}

function PcGuideMenus(props: { menus: NonNullable<Menus> }) {
  const [activeTab, setActiveTab] = useState<Tab>('A_LA_CARTE')

  return (
    <>
      <MenuTabs value={activeTab} menus={props.menus} onClick={setActiveTab} />
      <ScrollableContentInFlex>
        {activeTab === 'COURSE' && (
          <div className={css({ overflowY: 'auto', h: 'full' })}>
            <div className={vstack({ bgColor: 'gray.200', gap: '5', alignItems: 'stretch' })}>
              <Courses />
              {props.menus.course && (
                <Menu title="その他のコース" tab="COURSE" menu={props.menus.course} />
              )}
            </div>
          </div>
        )}
        {props.menus.aLaCarte && activeTab === 'A_LA_CARTE' && (
          <div className={css({ overflowY: 'auto', h: 'full' })}>
            <Menu title="" tab="A_LA_CARTE" menu={props.menus.aLaCarte} />
          </div>
        )}
        {props.menus.drink && activeTab === 'DRINK' && (
          <div className={css({ overflowY: 'auto', h: 'full' })}>
            <Menu title="" tab="DRINK" menu={props.menus.drink} />
          </div>
        )}
      </ScrollableContentInFlex>
    </>
  )
}

function SdGuideMenus(props: { menus: NonNullable<Menus> }) {
  const {
    rootRef,
    courseRef,
    aLaCarteRef,
    aLaCarteHeaderRef,
    drinkRef,
    drinkHeaderRef,
    activeTab,
    scrollToMenu,
  } = useScroll(props.menus)

  return (
    <>
      <MenuTabs value={activeTab} menus={props.menus} onClick={scrollToMenu} />
      <ScrollableContentInFlex>
        <div
          className={vstack({
            bgColor: 'gray.200',
            gap: '5',
            alignItems: 'stretch',
            h: 'full',
            overflowY: 'auto',
          })}
          ref={rootRef}
        >
          <div
            className={vstack({ bgColor: 'gray.200', gap: '5', alignItems: 'stretch' })}
            data-tab="COURSE"
            ref={courseRef}
          >
            <Courses />
            {props.menus.course && (
              <Menu title="その他のコース" tab="COURSE" menu={props.menus.course} />
            )}
          </div>
          {props.menus.aLaCarte && (
            <Menu
              title="料理"
              tab="A_LA_CARTE"
              menu={props.menus.aLaCarte}
              ref={aLaCarteRef}
              headerRef={aLaCarteHeaderRef}
            />
          )}
          {props.menus.drink && (
            <Menu
              title="ドリンク"
              tab="DRINK"
              menu={props.menus.drink}
              ref={drinkRef}
              headerRef={drinkHeaderRef}
            />
          )}
        </div>
      </ScrollableContentInFlex>
    </>
  )
}

function useScroll(menus: NonNullable<Menus>) {
  const rootRef = useRef<HTMLDivElement>(null)
  const courseRef = useRef<HTMLDivElement>(null)
  const aLaCarteRef = useRef<HTMLDivElement>(null)
  const aLaCarteHeaderRef = useRef<HTMLDivElement>(null)
  const drinkRef = useRef<HTMLDivElement>(null)
  const drinkHeaderRef = useRef<HTMLDivElement>(null)

  // すべてが画面上にスクロールしてしまっているかを監視
  const [outOfView, setOutOfView] = useState<Record<Tab, boolean>>({
    COURSE: false,
    A_LA_CARTE: false,
    DRINK: false,
  })
  useIntersectionObserver(
    menus,
    { aLaCarte: aLaCarteRef, drink: drinkRef },
    setOutOfView,
    rootRef,
    // 上方に100倍、下方にマイナスの画面高を指定することで上方だけを監視
    useMemo(() => ({ rootMargin: '10000% 0px -100%', threshold: 1 }), [])
  )

  // 画面上部 50% にヘッダーが入っているかを監視
  const [headerInView, setHeaderInView] = useState<Record<Tab, boolean>>({
    COURSE: true, // コースは必ず最上位に存在するので true にしておく
    A_LA_CARTE: false,
    DRINK: false,
  })
  useIntersectionObserver(
    menus,
    { aLaCarte: aLaCarteHeaderRef, drink: drinkHeaderRef },
    setHeaderInView,
    rootRef,
    useMemo(() => ({ rootMargin: '10000% 0px -50%', threshold: 1 }), [])
  )

  // 画面内にすべてが入っているかを監視
  const [inView, setInView] = useState<Record<Tab, boolean>>({
    COURSE: false,
    A_LA_CARTE: false,
    DRINK: false,
  })
  useIntersectionObserver(
    menus,
    { aLaCarte: aLaCarteRef, drink: drinkRef },
    setInView,
    rootRef,
    useMemo(() => ({ threshold: 1 }), [])
  )

  // - 画面上方にスクロールアウトしていなくて、
  // - ヘッダが上部50%以上にある
  // - もしくは画面内に100%入っているもの (一番下のものが小さいときヘッダが 50% より上に行かないことがあることの救済)
  // のうち一番下のものを選択する
  const activeTab =
    TABS.toReversed()
      .map((t) => t.value)
      .find((tab) => !outOfView[tab] && (headerInView[tab] || inView[tab])) ?? 'COURSE'

  const scrollToMenu = useCallback((value: Tab) => {
    switch (value) {
      case 'COURSE':
        courseRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      case 'A_LA_CARTE':
        aLaCarteRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      case 'DRINK':
        drinkRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
    }
  }, [])

  return {
    rootRef,
    courseRef,
    aLaCarteRef,
    aLaCarteHeaderRef,
    drinkRef,
    drinkHeaderRef,
    activeTab,
    scrollToMenu,
  }
}

function useIntersectionObserver(
  menus: NonNullable<Menus>,
  refs: {
    aLaCarte: RefObject<HTMLDivElement>
    drink: RefObject<HTMLDivElement>
  },
  set: React.Dispatch<React.SetStateAction<Record<Tab, boolean>>>,
  rootRef: RefObject<HTMLDivElement>,
  options: IntersectionObserverInit
) {
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    if (!rootRef.current) {
      // wait DOM ready
      setTimeout(() => setRetry((prev) => prev + 1), 0)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const tab = entry.target.getAttribute('data-tab') as Tab
          set((prev) => ({ ...prev, [tab]: entry.isIntersecting }))
        }
      },
      { ...options, root: rootRef.current }
    )
    if (menus.aLaCarte) {
      observer.observe(refs.aLaCarte.current!)
    }
    if (menus.drink) {
      observer.observe(refs.drink.current!)
    }
    return () => {
      observer.disconnect()
    }
  }, [
    menus.aLaCarte,
    menus.drink,
    refs.aLaCarte,
    refs.drink,
    set,
    rootRef,
    options,
    retry,
    setRetry,
  ])
}
